import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { toHex, getLuminance } from 'color2k';
import { HexColorPicker } from 'react-colorful';
import useEyeDropper from 'use-eye-dropper';
import { useDebouncedValue } from '../../hooks';
import { CheckIconSmall, EyedropIconSmall } from '../../icon';
import { NakedButton } from '../../naked';
import { PrimaryButton } from '../buttons';
import { TextField, useControlledField } from '../forms';
import { SkeletonLoader } from '../loader';
import { Tabs } from '../tabs';
import {
  articleDefaultWidthStyle,
  colorSwatchStyle,
  customColorPickerContainerStyle,
  eyeDropperButtonStyle,
  hexColorPickerStyle,
  hexInputContainerStyle,
  paletteButtonSpanStyle,
  paletteButtonStyle,
  paletteContainerSquareStyle,
  paletteContainerStyle,
  paletteGridStyle,
  skeletonStyle,
  tabsStyle,
  textFieldStyle,
} from './color-picker.styles';
import { defaultPalette } from './constants';
import { useColorPicker } from './provider';
import { generateColorProgression } from './utils';

export const ColorPickerMain = () => {
  const { hideCustom, hidePalette, onChange, activeColor, onClick, shouldApply, customColumns } = useColorPicker();
  const [controlledTab, setControlledTab] = useState(hidePalette ? 'custom' : 'palette');

  return (
    <article className={cx(!customColumns && articleDefaultWidthStyle)}>
      <Tabs className={tabsStyle} controlledTab={controlledTab} onChange={(id) => setControlledTab(id)}>
        <Tabs.Bar fullWidth>
          {!hidePalette && <Tabs.Tab id='palette'>Palette</Tabs.Tab>}
          {!hideCustom && <Tabs.Tab id='custom'>Custom</Tabs.Tab>}
        </Tabs.Bar>
        {controlledTab === 'palette' && !hidePalette && (
          <div className={cx(paletteContainerStyle, !customColumns && paletteContainerSquareStyle)}>
            <Palette onChange={shouldApply ? undefined : onChange} />
          </div>
        )}
        {controlledTab === 'custom' && !hideCustom && (
          <CustomColorPicker onChange={shouldApply ? undefined : onChange} />
        )}
      </Tabs>
      {shouldApply && (
        <PrimaryButton
          onClick={() => {
            onChange?.(activeColor);
            onClick?.();
          }}
        >
          Apply Color
        </PrimaryButton>
      )}
    </article>
  );
};

const CustomColorPicker = ({ onChange }: { onChange?: (color: string) => void }) => {
  const { activeColor, setActiveColor } = useColorPicker();

  return (
    <div className={customColorPickerContainerStyle}>
      <HexColorPicker
        color={activeColor}
        onChange={(newColor: string) => {
          setActiveColor(newColor);
          onChange?.(newColor);
        }}
        className={hexColorPickerStyle}
      />
      <HexInput onChange={onChange} />
    </div>
  );
};

const HexInput = ({ onChange }: { onChange?: (color: string) => void }) => {
  const { activeColor, setActiveColor } = useColorPicker();
  const { open, isSupported } = useEyeDropper();

  const pickColor = useCallback(() => {
    const openPicker = async () => {
      try {
        const color = await open();
        setActiveColor(color.sRGBHex);
        onChange?.(color.sRGBHex);
      } catch (e) {
        console.error(e);
      }
    };
    openPicker();
  }, [open]);

  const textField = useControlledField({ type: 'text', value: activeColor.toUpperCase(), onChange: setActiveColor });

  const debouncedValue = useDebouncedValue(textField.value, 500);

  const swatchStyle = {
    '--color-picker-swatch-background': activeColor,
  } as CSSProperties;

  useEffect(() => {
    // adds the '#' if it's missing and validates the color
    const validatedColor = CSS.supports('color', debouncedValue)
      ? debouncedValue
      : CSS.supports('color', `#${debouncedValue}`)
      ? `#${debouncedValue}`
      : '';
    if (validatedColor) {
      // this loses focuses when the input updates with the new value
      onChange?.(validatedColor);
    }
  }, [debouncedValue]);

  return (
    <div className={hexInputContainerStyle}>
      Hex:
      <TextField
        className={textFieldStyle}
        name='hex-input'
        startAdornment={<div className={colorSwatchStyle} style={swatchStyle} />}
        label=''
        {...textField}
      />
      {isSupported() && (
        <NakedButton className={eyeDropperButtonStyle} onClick={pickColor}>
          <EyedropIconSmall />
        </NakedButton>
      )}
    </div>
  );
};

const Palette = ({ onChange }: { onChange?: (color: string) => void }) => {
  const { extractedColors, isLoading, palette, customColumns } = useColorPicker();
  const showExtractedColor = !isLoading && !extractedColors.length;

  const gridStyle = {
    '--color-picker-palette-columns': `repeat(${customColumns ?? 6}, 1fr)`,
  } as CSSProperties;

  return (
    <div className={paletteGridStyle} style={gridStyle}>
      {isLoading &&
        new Array(36).fill(undefined).map((item) => <SkeletonLoader key={item} className={skeletonStyle} />)}

      {!showExtractedColor &&
        extractedColors?.slice(0, 6)?.map((val) =>
          generateColorProgression(val).map((color) => {
            return <PaletteButton key={color} color={color} onChange={onChange} />;
          })
        )}
      {!palette &&
        showExtractedColor &&
        defaultPalette.map((color) => <PaletteButton key={color} color={color} onChange={onChange} />)}
      {palette &&
        showExtractedColor &&
        palette.map((color) => <PaletteButton key={color} color={color} onChange={onChange} />)}
    </div>
  );
};

const PaletteButton = ({ color, onChange }: { color: string; onChange?: (color: string) => void }) => {
  const { setActiveColor, activeColor } = useColorPicker();
  const hexColor = toHex(color);

  const swatchStyle = {
    '--color-picker-palette-button-background': color,
  } as CSSProperties;

  return (
    <NakedButton
      onClick={() => {
        setActiveColor(hexColor);
        onChange?.(hexColor);
      }}
      className={paletteButtonStyle}
    >
      <span className={paletteButtonSpanStyle} style={swatchStyle}>
        {activeColor === hexColor && <CheckIconSmall color={getLuminance(color) > 0.7 ? 'default' : 'white'} />}
      </span>
    </NakedButton>
  );
};
